import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { tagApi } from '../api/tags';
import { useAuth } from '../context/AuthContext';
import type { Tag } from '../types';

const TicketTags: React.FC<{ ticketId: string; onChange?: () => void }> = ({ ticketId, onChange }) => {
  const { user } = useAuth();
  const isStaff = user?.roles?.includes('ADMIN') || user?.roles?.includes('EMPLOYEE_ADMIN') || user?.roles?.includes('EMPLOYEE');
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [ticketTags, setTicketTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [all, mine] = await Promise.all([
        tagApi.list(),
        tagApi.forTicket(ticketId),
      ]);
      setAllTags(all.data);
      setTicketTags(mine.data);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  const available = useMemo(() => {
    const assigned = new Set(ticketTags.map((t) => t.id));
    return allTags.filter((t) => !assigned.has(t.id));
  }, [allTags, ticketTags]);

  const add = async (tagId: string) => {
    const { data } = await tagApi.addToTicket(ticketId, tagId);
    setTicketTags(data);
    setShowPicker(false);
    onChange?.();
  };

  const remove = async (tagId: string) => {
    const { data } = await tagApi.removeFromTicket(ticketId, tagId);
    setTicketTags(data);
    onChange?.();
  };

  if (loading) return null;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.375rem' }}>
      {ticketTags.length === 0 && !isStaff && (
        <span className="item-meta" style={{ fontSize: '0.75rem' }}>Brak tagów</span>
      )}
      {ticketTags.map((t) => (
        <TagChip key={t.id} tag={t} onRemove={isStaff ? () => remove(t.id) : undefined} />
      ))}
      {isStaff && (
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setShowPicker((v) => !v)}
            className="btn btn-primary btn-sm"
            disabled={available.length === 0}
            title={available.length === 0 ? 'Wszystkie tagi już dodane' : 'Dodaj tag'}
          >
            Tag
          </button>
          {showPicker && available.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              padding: '0.375rem',
              zIndex: 20,
              minWidth: 180,
              maxHeight: 240,
              overflowY: 'auto',
            }}>
              {available.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => add(t.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    padding: '0.25rem 0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                    borderRadius: 4,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-surface-alt)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <span style={{
                    display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                    background: t.color || '#5a636e', marginRight: 6, verticalAlign: 'middle',
                  }} />
                  {t.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const TagChip: React.FC<{ tag: Tag; onRemove?: () => void }> = ({ tag, onRemove }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: (tag.color || '#5a636e') + '20',
    color: tag.color || '#5a636e',
    border: `1px solid ${tag.color || '#d7dade'}`,
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '0.125rem 0.5rem',
    borderRadius: 12,
  }}>
    {tag.name}
    {onRemove && (
      <button
        type="button"
        onClick={onRemove}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          padding: 0,
          fontSize: '0.875rem',
          lineHeight: 1,
        }}
        title="Usuń tag"
      >
        ×
      </button>
    )}
  </span>
);

export default TicketTags;
